import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, del, get, patch, post } from '../api';
import { useMe } from '../auth';
import {
  Alert,
  Card,
  Empty,
  fmtNum,
  fmtRelative,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
  Toggle,
  useAction,
  useLoader,
} from '../components/ui';
import type { Policy } from '../types';

type Mode = 'url' | 'file' | 'text';
const ACCEPT = '.pdf,.docx,.txt,.md,.markdown,.html,.htm,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/html';

function AddSource({ onAdded }: { onAdded: () => void }) {
  const [mode, setMode] = useState<Mode>('url');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const act = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await act.run('add', async () => {
      if (mode === 'url') {
        await post('/api/policies/url', { url: url.trim(), ...(name.trim() ? { name: name.trim() } : {}) });
      } else if (mode === 'text') {
        await post('/api/policies/text', { name: name.trim(), content });
      } else {
        if (!file) throw new Error('Choose a file to upload');
        const form = new FormData();
        // The server reads fields that arrive before the file part, so `name` goes first.
        if (name.trim()) form.append('name', name.trim());
        form.append('file', file);
        await api('/api/policies/upload', { method: 'POST', body: form });
      }
      return true;
    });
    if (ok) {
      setName('');
      setUrl('');
      setContent('');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      onAdded();
    }
  };

  return (
    <Card title="Add a source" subtitle="Your terms of use are given to the model as policy context for every message.">
      <div className="tabs" role="tablist">
        {(
          [
            ['url', 'Website URL'],
            ['file', 'Upload document'],
            ['text', 'Paste text'],
          ] as [Mode, string][]
        ).map(([m, label]) => (
          <button key={m} role="tab" aria-selected={mode === m} className={mode === m ? 'active' : ''} onClick={() => setMode(m)} type="button">
            {label}
          </button>
        ))}
      </div>
      <form className="form" onSubmit={submit}>
        <Alert>{act.error}</Alert>
        {mode === 'url' && (
          <label className="field">
            <span>Page URL</span>
            <input type="url" required placeholder="https://example.com/terms" value={url} onChange={(e) => setUrl(e.target.value)} />
            <span className="hint">The page is fetched and converted to text. Use "Refresh" later when it changes.</span>
          </label>
        )}
        {mode === 'file' && (
          <label className="field">
            <span>Document</span>
            <input ref={fileRef} type="file" required accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
            <span className="hint">PDF, DOCX, TXT, Markdown or HTML.</span>
          </label>
        )}
        <label className="field">
          <span>Name {mode === 'text' ? '' : <span className="muted">(optional)</span>}</span>
          <input
            required={mode === 'text'}
            maxLength={200}
            placeholder={mode === 'url' ? 'Defaults to the hostname' : mode === 'file' ? 'Defaults to the file name' : 'Community guidelines'}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {mode === 'text' && (
          <label className="field">
            <span>Policy text</span>
            <textarea required rows={8} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste your rules here…" />
            <span className="hint">{fmtNum(content.length)} characters</span>
          </label>
        )}
        <div className="btn-row">
          <button className="btn btn-primary" disabled={act.busy === 'add'}>
            {act.busy === 'add' ? 'Adding…' : 'Add source'}
          </button>
        </div>
      </form>
    </Card>
  );
}

function ViewText({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, error } = useLoader(() => get<Policy>(`/api/policies/${id}`), [id]);
  return (
    <Modal title={data ? data.name : 'Extracted text'} onClose={onClose} wide>
      <Alert>{error}</Alert>
      {!data ? (
        !error && <Spinner />
      ) : (
        <>
          <p className="small muted">
            {fmtNum(data.char_count)} characters · {data.kind}
            {data.source_url && (
              <>
                {' '}·{' '}
                <a href={data.source_url} target="_blank" rel="noreferrer noopener">
                  {data.source_url}
                </a>
              </>
            )}
          </p>
          {data.content_text ? <pre className="policy-text">{data.content_text}</pre> : <Empty>No text extracted yet.</Empty>}
        </>
      )}
    </Modal>
  );
}

export function PoliciesPage() {
  const { isAdmin } = useMe();
  const { data, error, loading, reload } = useLoader(() => get<{ policies: Policy[] }>('/api/policies'), []);
  const act = useAction();
  const [viewing, setViewing] = useState<string | null>(null);

  const policies = data?.policies ?? [];
  const anyPending = policies.some((p) => p.status === 'pending');

  // Poll while sources are being fetched/extracted.
  useEffect(() => {
    if (!anyPending) return;
    const t = setInterval(() => void reload(true), 2000);
    return () => clearInterval(t);
  }, [anyPending, reload]);

  const toggle = (p: Policy, enabled: boolean) =>
    act.run(`toggle-${p.id}`, async () => {
      await patch(`/api/policies/${p.id}`, { enabled });
      await reload(true);
    });
  const refresh = (p: Policy) =>
    act.run(`refresh-${p.id}`, async () => {
      await post(`/api/policies/${p.id}/refresh`);
      await reload(true);
    });
  const remove = (p: Policy) => {
    if (!window.confirm(`Delete "${p.name}"? It will no longer be used for moderation.`)) return;
    void act.run(`del-${p.id}`, async () => {
      await del(`/api/policies/${p.id}`);
      await reload(true);
    });
  };

  const totalChars = policies.filter((p) => p.enabled && p.status === 'ready').reduce((s, p) => s + p.char_count, 0);

  return (
    <>
      <PageHeader title="Terms & policies" subtitle="The rules your community agreed to. Enabled, ready sources are used when moderating." />
      <Alert>{error ?? act.error}</Alert>
      {isAdmin && <AddSource onAdded={() => void reload(true)} />}
      <Card
        title="Sources"
        subtitle={`${policies.length} source${policies.length === 1 ? '' : 's'} · ${fmtNum(totalChars)} characters in use`}
        className="card-flush"
        actions={anyPending && <span className="small muted"><span className="spinner spinner-sm" /> Processing…</span>}
      >
        {loading && !data ? (
          <Spinner />
        ) : policies.length === 0 ? (
          <Empty>No terms added yet. Without them, moderation relies on general safety categories and your custom instructions.</Empty>
        ) : (
          <ul className="policy-list">
            {policies.map((p) => (
              <li key={p.id} className={p.enabled ? '' : 'is-dim'}>
                <div className="policy-main">
                  <div className="policy-title">
                    <span className="kind-tag">{p.kind === 'url' ? 'URL' : p.kind === 'document' ? 'DOC' : 'TEXT'}</span>
                    <span className="strong">{p.name}</span>
                    <StatusBadge status={p.status} />
                  </div>
                  <div className="small muted policy-sub">
                    {p.source_url ? (
                      <a href={p.source_url} target="_blank" rel="noreferrer noopener" className="ellipsis">
                        {p.source_url}
                      </a>
                    ) : (
                      p.filename && <span className="ellipsis">{p.filename}</span>
                    )}
                    <span>{fmtNum(p.char_count)} chars</span>
                    <span>updated {fmtRelative(p.updated_at)}</span>
                  </div>
                  {p.status === 'failed' && p.error && <div className="small tone-text-bad">{p.error}</div>}
                </div>
                <div className="policy-actions">
                  <Toggle checked={p.enabled} disabled={!isAdmin || act.busy === `toggle-${p.id}`} onChange={(v) => toggle(p, v)} label="Enabled" />
                  <button className="btn btn-sm btn-ghost" disabled={p.status !== 'ready'} onClick={() => setViewing(p.id)}>
                    View text
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        className="btn btn-sm"
                        disabled={p.status === 'pending' || act.busy === `refresh-${p.id}`}
                        onClick={() => refresh(p)}
                        title={p.kind === 'url' ? 'Re-fetch the page' : 'Re-extract the text'}
                      >
                        Refresh
                      </button>
                      <button className="btn btn-sm btn-danger-ghost" disabled={act.busy === `del-${p.id}`} onClick={() => remove(p)}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {viewing && <ViewText id={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
